import {useQueryClient} from '@tanstack/react-query'
import {useAddNotification} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {getErrorMessage} from '@yandex-delivery/utils'
import {useCallback} from 'react'
import {useFormContext} from 'react-hook-form'

import {fetchFormLayout} from '../../../api'
import type {Descriptors, Widgets} from '../../../api/definitions/form'
import {useConfigContext} from '../../../contexts'
import {store} from '../../../store'
import {toFormRequestBody} from '../lib'
import {matchPattern} from '../lib/matcher'

const mode = 'b2b_external_order' // TODO перенести в конфиг

/**
 * Возвращает фетчер конфига формы.
 */
export function useGetDeliveryFormData(clearDraftValues: VoidFunction) {
  const i18n = useI18n()
  const addNotification = useAddNotification()
  const queryClient = useQueryClient()
  const config = useConfigContext()
  const {watch} = useFormContext()

  return useCallback(async (): Promise<Widgets | {}> => {
    const descriptors = store.state.descriptors

    /** Если в descriptor есть подходящий match и ttl оффера не истек, вместо запроса используется layout из него */
    const descriptorMatched = descriptors?.find(descriptor => matchPattern(descriptor.match, store.state.formState))

    if (descriptorMatched && store.state.ttl !== 0) {
      store.setState(prev => ({
        ...prev,
        offerId: descriptorMatched.override.offer?.offer_id,
      }))

      return descriptorMatched.override.layout as Widgets
    }

    let data: Descriptors = []

    try {
      const body = {
        mode,
        user_info: store.state.user_info,
        state: toFormRequestBody(watch(), config),
      }
      data = await fetchFormLayout(config, body, config.countryCode)
    } catch (error: unknown) {
      addNotification(
        error && error instanceof Error ? getErrorMessage(error) : i18n('request.mutation.error', 'Ошибка запроса'),
        {
          type: 'error',
        },
      )

      // При ошибке запроса очищаем драфт
      clearDraftValues()
    }

    const ttl = data?.[0]?.override?.offer?.ttl
    if (data) {
      store.setState(prev => ({
        ...prev,
        descriptors: data,
        offerId: data?.[0]?.override?.offer?.offer_id,
        ttl,
      }))
    }

    if (ttl) {
      setTimeout(() => {
        /** сбрасываю ttl оффера, чтобы перезапрос не прервался на матчере */
        store.state.ttl = 0
        void queryClient.invalidateQueries({queryKey: ['orderForm']})
      }, ttl * 1000)
    }

    return data || {}
  }, [addNotification, clearDraftValues, config, i18n, queryClient, watch])
}
